<template>
  <header class="app-header">
    <div class="header-content">
      <div class="logo">
        <svg class="logo-svg" width="32" height="32" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="18" cy="18" r="17" stroke="currentColor" stroke-width="2"/>
          <circle cx="18" cy="18" r="10" stroke="currentColor" stroke-width="2"/>
          <circle cx="18" cy="18" r="3" fill="currentColor"/>
        </svg>
        <span class="logo-text">Discogs Collection</span>
      </div>

      <nav v-if="isAuthenticated" class="main-nav" aria-label="Main">
        <router-link to="/" class="nav-link">Collection</router-link>
        <router-link to="/shuffle" class="nav-link">Shuffle</router-link>
        <router-link to="/tagging" class="nav-link">Tagging</router-link>
      </nav>

      <div v-if="isAuthenticated" class="search" role="search">
        <div class="search-field">
          <label for="search-bar" class="visually-hidden">Search</label>
          <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20">
            <path d="M0 0h24v24H0z" fill="none"/>
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91
              16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5
              9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            id="search-bar"
            class="search-bar"
            type="text"
            v-model="searchInput"
            :placeholder="searchAllDiscogs ? 'Search all of Discogs…' : 'Search your collection…'"
            @input="handleSearchInput"
          />
        </div>

        <div class="scope" role="group" aria-label="Search scope">
          <button
            type="button"
            class="scope-btn"
            :class="{ active: !searchAllDiscogs }"
            :aria-pressed="!searchAllDiscogs"
            @click="setScope(false)"
          >Mine</button>
          <button
            type="button"
            class="scope-btn"
            :class="{ active: searchAllDiscogs }"
            :aria-pressed="searchAllDiscogs"
            @click="setScope(true)"
          ><span class="scope-extra">All </span>Discogs</button>
        </div>
      </div>

      <div v-if="isAuthenticated" class="account">
        <template v-if="userInfo">
          <img
            class="avatar"
            :src="userInfo.avatar_url || 'https://www.discogs.com/images/default-user.png'"
            alt=""
          />
          <span class="username">{{ userInfo.username }}</span>
        </template>
        <button
          type="button"
          @click="handleLogout"
          class="logout-btn"
          aria-label="Log out"
          title="Log out"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20">
            <path d="M0 0h24v24H0z" fill="none"/>
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0
              1.1.9 2 2 2h8v-2H4V5z"/>
          </svg>
        </button>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, watch } from 'vue';
import useAuth from '@/composables/useAuth';
import debounce from 'lodash.debounce';

// Define emits
const emit = defineEmits(['logout', 'search']);

// Get auth state from composable
const {
  user, discogsAccount, isAuthenticated, logout,
} = useAuth();

const userInfo = ref(null);
const searchInput = ref('');
const searchAllDiscogs = ref(false);

// Helper to emit search event with all params
const emitSearchEvent = () => {
  emit('search', {
    query: searchInput.value,
    searchAllDiscogs: searchAllDiscogs.value,
  });
};

// Debounce search to avoid too many events
const handleSearchInput = debounce(() => {
  emitSearchEvent();
}, 300);

// Switch between searching your collection and all of Discogs
const setScope = allDiscogs => {
  if (searchAllDiscogs.value === allDiscogs) return;
  searchAllDiscogs.value = allDiscogs;
  emitSearchEvent();
};

// Empties the box from outside (the results page's Clear button)
const clearSearch = () => {
  searchInput.value = '';
  handleSearchInput.cancel();
  emitSearchEvent();
};

// Logout handler
const handleLogout = async () => {
  try {
    await logout();
    emit('logout');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error signing out:', error);
  }
};

// Watch for changes in user and discogsAccount
watch([user, discogsAccount], ([newUser, newDiscogsAccount]) => {
  if (newUser && newDiscogsAccount) {
    userInfo.value = {
      username: newDiscogsAccount.username || newUser.displayName || 'User',
      avatar_url: newUser.photoURL,
      collection_count: 0, // Will be updated when we fetch collection data
    };
  } else {
    userInfo.value = null;
  }
}, { immediate: true });

// Update authentication status when a user logs in
const onLoginSuccess = async data => {
  // This is called from parent component after login
  if (data && data.collection_count) {
    userInfo.value = {
      ...userInfo.value,
      collection_count: data.collection_count,
    };
  }
};

// Expose to parent component
defineExpose({
  onLoginSuccess,
  clearSearch,
});
</script>

<style scoped>
.app-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: var(--color-surface);
  box-shadow: 0 1px 0 var(--color-divider);
}

/* One grid for the whole header. Narrow screens drop the search onto its own row */
.header-content {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "brand nav account"
    "search search search";
  align-items: center;
  gap: 0.6rem 1rem;
  max-width: var(--page-max);
  margin: 0 auto;
  padding: 0.7rem var(--page-gutter);
}

.logo {
  grid-area: brand;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--color-text);
  font-weight: 700;
  font-size: 1rem;
  white-space: nowrap;
}

.logo-text {
  display: none;
}

.logo-svg {
  color: var(--color-primary);
}

.main-nav {
  grid-area: nav;
  display: flex;
  gap: 4px;
  min-width: 0;
}

.nav-link {
  color: var(--color-text-secondary);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 14px;
  text-decoration: none;
  white-space: nowrap;
}

.nav-link:hover {
  color: var(--color-text);
  text-decoration: none;
}

.nav-link.router-link-exact-active {
  color: var(--color-text);
  background: var(--color-surface-light);
}

.nav-link:focus-visible,
.scope-btn:focus-visible,
.logout-btn:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.account {
  grid-area: account;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--color-primary);
}

.username {
  display: none;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--color-text);
}

.logout-btn {
  background: transparent;
  border: none;
  padding: 0.4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color var(--transition-base);
}

.logout-btn:hover {
  background-color: var(--color-hover);
}

.logout-btn svg {
  fill: var(--color-text);
}

/* Search: the field grows, the scope switch sits beside it rather than inside it */
.search {
  grid-area: search;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  max-width: 640px;
  justify-self: center;
}

.search-field {
  position: relative;
  flex: 1;
  min-width: 0;
}

.search-bar {
  width: 100%;
  padding: 0.55rem 1rem 0.55rem 2.5rem;
  border-radius: var(--border-radius-pill);
  border: 1px solid transparent;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--color-text);
  font-size: 0.9rem;
  transition: all var(--transition-base);
}

.search-bar::placeholder {
  color: var(--color-text-secondary);
}

.search-bar:focus {
  outline: none;
  background-color: rgba(255, 255, 255, 0.15);
  border-color: var(--color-primary);
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  fill: var(--color-text-secondary);
  pointer-events: none;
}

.scope {
  display: flex;
  flex-shrink: 0;
  padding: 3px;
  border-radius: var(--border-radius-pill);
  background-color: rgba(255, 255, 255, 0.1);
}

.scope-btn {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 0.8rem;
  border-radius: var(--border-radius-pill);
  white-space: nowrap;
  transition: background-color var(--transition-base), color var(--transition-base);
}

.scope-btn:hover {
  color: var(--color-text);
}

.scope-btn.active {
  background-color: var(--color-text);
  color: #000;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Phones: tighter nav and a shorter scope label so the search field keeps room to breathe */
@media (max-width: 479px) {
  .header-content {
    gap: 0.5rem 0.5rem;
  }

  .nav-link {
    padding: 6px 9px;
  }

  .scope-btn {
    padding: 0.3rem 0.6rem;
  }

  .scope-extra {
    display: none;
  }

  .search {
    gap: 0.4rem;
  }
}

@media (min-width: 480px) {
  .logo-text {
    display: block;
  }
}

@media (min-width: 640px) {
  .username {
    display: block;
  }
}

/* Wide enough for everything on one row: brand | nav | search | account */
@media (min-width: 960px) {
  .header-content {
    grid-template-columns: auto auto minmax(0, 1fr) auto;
    grid-template-areas: "brand nav search account";
    gap: 1rem 1.5rem;
    padding-block: 0.85rem;
  }
}
</style>
